import { Alert, Box, Button, Collapse, Typography, Stack, Chip, Link, TypographyVariant, Tooltip } from '@mui/material';
import { Lock, AccessTime } from '@mui/icons-material';

import useThemeBreakpoint from '../../useThemeBreakpoint';

import { LaunchpadItem, LaunchpadSubtask, TaskStatus } from './LaunchpadProgress.interface';
import { getStatusConfig } from './LaunchpadProgress.constants';
import StatusIcon from './StatusIcon';
import LaunchpadSubtaskComponent from './LaunchpadSubtask';
import { getLaunchpadCtaLabel, isLaunchpadItemCancelled, isLaunchpadItemCompleted } from './launchpadItemState';
import useStartLaunchpadTask from './useStartLaunchpadTask';

interface LaunchpadItemProps {
    item: LaunchpadItem;
    index: number;
    totalItems: number;
    expandedItems: Set<string>;
    onToggleExpanded: (itemId: string) => void;
    onItemClick: (item: LaunchpadItem) => void;
    onSubtaskClick?: (subtask: LaunchpadSubtask) => void;
    getMostRecentNotCompletedTask: (item: LaunchpadItem) => LaunchpadItem | LaunchpadSubtask | null;
    isItemBlocked: (item: LaunchpadItem) => boolean;
    titleVariant?: TypographyVariant;
    descriptionVariant?: TypographyVariant;
    inlineExpandToggle?: boolean;
}

interface ExpandToggleButtonProps {
    isExpanded: boolean;
    onClick: () => void;
}

function ExpandToggleButton({ isExpanded, onClick }: ExpandToggleButtonProps) {
    return (
        <Button
            variant="text"
            size="small"
            onClick={onClick}
            color="primary"
            sx={{ typography: 'body2', pl: 0, '&:hover': { background: 'transparent' } }}>
            {isExpanded ? 'View less' : 'View details'}
        </Button>
    );
}

function InlineExpandToggle({ isExpanded, onClick }: ExpandToggleButtonProps) {
    return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
            component="button"
            type="button"
            variant="body2"
            color="primary"
            underline="none"
            onClick={onClick}
            sx={{ ml: 0.5, verticalAlign: 'unset' }}>
            {isExpanded ? 'View less' : 'View details'}
        </Link>
    );
}

function LaunchpadItemComponent({
    item,
    index,
    totalItems,
    expandedItems,
    onToggleExpanded,
    onItemClick,
    onSubtaskClick = undefined,
    getMostRecentNotCompletedTask,
    isItemBlocked,
    titleVariant = 'subtitle1',
    descriptionVariant = 'body1',
    inlineExpandToggle = false,
}: LaunchpadItemProps) {
    const config = getStatusConfig(item.status);
    const IconComponent = config.icon;
    const isBlocked = isItemBlocked(item);
    const hasSubtasks = item.subtasks.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const completedSubtasks = item.subtasks.filter((s) => s.status === TaskStatus.COMPLETED).length;
    const breakpoint = useThemeBreakpoint();
    const startTask = useStartLaunchpadTask(onItemClick, onSubtaskClick);

    const isCompleted = isLaunchpadItemCompleted(item);
    const isCancelled = isLaunchpadItemCancelled(item);

    const mostRecentTask = getMostRecentNotCompletedTask(item);
    const hasCta = mostRecentTask && mostRecentTask?.cta_url;
    const isCurrentStep = mostRecentTask?.id === item.id;
    const shouldShowDescription = !hasSubtasks || (!isExpanded && !isCurrentStep);
    const canToggleSubtasks = hasSubtasks && !isCompleted && !isCancelled;

    const onClickCTA = () => startTask(mostRecentTask);

    return (
        <Stack
            direction="row"
            spacing={3}
            sx={{
                px: 3,
                py: 2,
                ...(index < totalItems - 1 && {
                    borderBottomWidth: '1px',
                    borderBottomStyle: 'solid',
                    borderBottomColor: 'divider',
                }),
            }}>
            <StatusIcon
                status={item.status}
                index={index}
                hasSubtasks={hasSubtasks}
                completedSubtasks={completedSubtasks}
                totalSubtasks={item.subtasks.length}>
                {item.status === TaskStatus.COMPLETED && <IconComponent fontSize="small" />}
            </StatusIcon>

            <Box sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant={titleVariant}
                            color="text.primary"
                            sx={{ lineHeight: shouldShowDescription && item.description ? 'normal' : 2.2 }}>
                            {item.name}
                        </Typography>
                        {shouldShowDescription &&
                            (inlineExpandToggle ? (
                                <Typography variant={descriptionVariant} color="text.secondary">
                                    {item.description}
                                    {canToggleSubtasks && (
                                        <>
                                            {item.description ? ' ' : ''}
                                            <InlineExpandToggle
                                                isExpanded={isExpanded}
                                                onClick={() => onToggleExpanded(item.id)}
                                            />
                                        </>
                                    )}
                                </Typography>
                            ) : (
                                <Stack direction="row" justifyContent="flex-start" alignItems="center">
                                    {item.description && (
                                        <Typography variant={descriptionVariant} color="text.secondary" sx={{ mr: 2 }}>
                                            {item.description}
                                        </Typography>
                                    )}
                                    {canToggleSubtasks && (
                                        <ExpandToggleButton
                                            isExpanded={isExpanded}
                                            onClick={() => onToggleExpanded(item.id)}
                                        />
                                    )}
                                </Stack>
                            ))}
                    </Box>

                    {hasCta && !isCompleted && !isCancelled && (
                        <Box sx={{ ml: 2, flexShrink: 0 }}>
                            <Tooltip title={isBlocked && item.lockedTooltip ? item.lockedTooltip : ''}>
                                <span>
                                    <Button
                                        variant={isBlocked ? 'outlined' : 'contained'}
                                        onClick={onClickCTA}
                                        sx={{
                                            width: '120px',
                                        }}
                                        disabled={isBlocked}
                                        startIcon={isBlocked ? <Lock /> : undefined}>
                                        {getLaunchpadCtaLabel(mostRecentTask, item, isBlocked)}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                    {!hasCta && !isCompleted && !isCancelled && !isBlocked && breakpoint !== 'small' && (
                        <Chip
                            icon={<AccessTime fontSize="small" />}
                            label="IN PROGRESS"
                            sx={{ backgroundColor: 'purple.100' }}
                        />
                    )}
                </Stack>
                {!hasCta && !isCompleted && !isCancelled && !isBlocked && breakpoint === 'small' && (
                    <Chip
                        icon={<AccessTime fontSize="small" />}
                        label="IN PROGRESS"
                        sx={{ my: 2, backgroundColor: 'purple.100' }}
                    />
                )}
                {item.alert && (
                    <Alert
                        severity={item.alert.severity}
                        sx={{ mt: 2, alignItems: 'center', '& .MuiAlert-icon svg': { fontSize: '1rem' } }}>
                        {item.alert.message}
                    </Alert>
                )}
                {canToggleSubtasks && (
                    <Collapse in={isExpanded}>
                        <div>
                            {item.subtasks.map((subtask, subtaskIndex) => (
                                <LaunchpadSubtaskComponent
                                    key={subtask.id}
                                    subtask={subtask}
                                    subtaskIndex={subtaskIndex}
                                    totalSubtasks={item.subtasks.length}
                                />
                            ))}
                        </div>
                    </Collapse>
                )}
                {canToggleSubtasks && isExpanded && (
                    <ExpandToggleButton isExpanded={isExpanded} onClick={() => onToggleExpanded(item.id)} />
                )}
            </Box>
        </Stack>
    );
}

export default LaunchpadItemComponent;
